/**
 * Shared sticky Add-to-Bag reveal mechanism.
 *
 * Owns ONLY the visibility state machine and its scroll controller — never the
 * bar's contents. Every consumer keeps its own content-sync code (price, title,
 * option values, submit state) in its own section.
 *
 * Extracted from sections/product.liquid so a second and third section do not
 * have to re-implement it. The presentational CSS for each bar's contents stays
 * with the section that owns those contents; the mechanism CSS lives unlayered
 * in src/css/theme.css alongside the --z-90 token.
 *
 * Per-section hooks it resolves (all pre-existing conventions):
 *   [data-product-section]          the owning section root
 *   [data-product-header-end]       the scroll landmark that reveals the bar
 *   [data-product-primary-actions]  the in-page action cluster the mobile bar defers to
 *   [data-sticky-atb-mobile-inner]  height source for the mobile bar
 *
 * Public API:
 *   window.StickyAtb.refresh(sectionEl)  recompute one section, idempotent and
 *                                        null-safe (a no-op before init).
 *
 * The module self-initialises on DOMContentLoaded rather than exposing an init()
 * a consumer calls synchronously: section {% javascript %} compiles into
 * scripts.js and runs BEFORE snippet-scripts.js, so a synchronous top-level
 * handshake would read as undefined on every page load.
 */
;(function () {
  'use strict';

  if (window.__stickyAtbReady) return;
  window.__stickyAtbReady = true;

  // The custom-bundle bar is driven by its own module
  // (assets/product-custom-bundle-sticky-atb.js) and is deliberately not
  // migrated here yet. Excluding it keeps exactly one controller per bar.
  var DESKTOP_SELECTOR = '[data-sticky-atb-desktop]:not([data-sticky-custom-bundle])';
  var MOBILE_SELECTOR = '[data-sticky-atb-mobile]:not([data-sticky-custom-bundle])';

  var controllers = [];
  var ticking = false;

  function setStickyVisibility(element, visible) {
    if (!element) return;
    element.setAttribute('data-visible', visible ? 'true' : 'false');
    if (visible) {
      element.removeAttribute('aria-hidden');
      element.removeAttribute('inert');
    } else {
      element.setAttribute('aria-hidden', 'true');
      element.setAttribute('inert', '');
    }
  }

  function createController(section) {
    var stickyDesktop = section.querySelector(DESKTOP_SELECTOR);
    var stickyMobile = section.querySelector(MOBILE_SELECTOR);
    if (!stickyDesktop && !stickyMobile) return null;

    var headerEnd = section.querySelector('[data-product-header-end]');
    var header = document.getElementById('site-header');

    // Resolved fresh on every call, not cached: a form-per-tab layout (the gift-card
    // PDP) renders one [data-product-primary-actions] per panel, only one of which
    // is ever visible, and the visible one changes on tab switch. A single
    // querySelector cached here would always return the first in DOM order — the
    // digital panel's — and getBoundingClientRect() on the other tab's hidden
    // element returns an all-zero rect, which getMainActionState() below would
    // misread as 'above-sticky' unconditionally. offsetParent is null for any
    // display:none ancestor, which is
    // exactly how the `hidden` class conceals the inactive panel.
    function resolveMainActionArea() {
      var areas = section.querySelectorAll('[data-product-primary-actions]');
      if (areas.length < 2) return areas[0] || null;
      for (var i = 0; i < areas.length; i++) {
        if (areas[i].offsetParent !== null) return areas[i];
      }
      return areas[0];
    }

    function getDesktopTop() {
      if (!header || !header.hasAttribute('data-sticky')) return 12;
      return header.offsetHeight + 24;
    }

    function desktopProductSectionEnded(topOffset) {
      if (!stickyDesktop) return false;
      return section.getBoundingClientRect().bottom <= topOffset + stickyDesktop.offsetHeight + 12;
    }

    function getMobileStickyHeight() {
      if (!stickyMobile) return 0;
      var inner = stickyMobile.querySelector('[data-sticky-atb-mobile-inner]');
      return stickyMobile.offsetHeight || (inner ? inner.offsetHeight : 0) || 96;
    }

    function mobileProductSectionEnded() {
      if (!stickyMobile) return false;
      var stickyHeight = getMobileStickyHeight();
      var sectionBottom = section.getBoundingClientRect().bottom;
      var stickyTop = window.innerHeight - stickyHeight;
      return sectionBottom <= stickyTop;
    }

    function getMainActionState() {
      var mainActionArea = resolveMainActionArea();
      if (!mainActionArea || !stickyMobile) return 'missing';
      var rect = mainActionArea.getBoundingClientRect();
      var stickyTop = window.innerHeight - getMobileStickyHeight();
      if (rect.top > stickyTop) {
        return 'below-sticky';
      }
      if (rect.bottom >= stickyTop) {
        return 'touching-sticky';
      }
      return 'above-sticky';
    }

    function productHeaderPassed() {
      if (!headerEnd) return false;
      return headerEnd.getBoundingClientRect().top <= 0;
    }

    function shouldShowMobileSticky() {
      if (!stickyMobile) return false;
      if (window.innerWidth >= 1024) return false;
      if (mobileProductSectionEnded()) return false;
      var actionState = getMainActionState();
      if (actionState === 'below-sticky') {
        return true;
      }
      if (actionState === 'touching-sticky') {
        return false;
      }
      if (actionState === 'above-sticky') {
        return productHeaderPassed();
      }
      return false;
    }

    function apply() {
      if (stickyDesktop) {
        var desktopTop = getDesktopTop();
        stickyDesktop.style.top = desktopTop + 'px';
        var showDesktop = false;
        if (window.innerWidth >= 1024 && headerEnd) {
          var headerEndTop = headerEnd.getBoundingClientRect().top;
          showDesktop = headerEndTop <= desktopTop && !desktopProductSectionEnded(desktopTop);
        }
        setStickyVisibility(stickyDesktop, showDesktop);
      }

      if (stickyMobile) {
        setStickyVisibility(stickyMobile, shouldShowMobileSticky());
      }
    }

    return { section: section, apply: apply };
  }

  function schedule(controller) {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      ticking = false;
      if (controller) {
        controller.apply();
        return;
      }
      for (var i = 0; i < controllers.length; i++) {
        controllers[i].apply();
      }
    });
  }

  function find(section) {
    for (var i = 0; i < controllers.length; i++) {
      if (controllers[i].section === section) return controllers[i];
    }
    return null;
  }

  function init() {
    controllers = [];

    var bars = document.querySelectorAll(DESKTOP_SELECTOR + ',' + MOBILE_SELECTOR);
    var seen = [];

    for (var i = 0; i < bars.length; i++) {
      var section = bars[i].closest('[data-product-section]');
      if (!section || seen.indexOf(section) !== -1) continue;
      seen.push(section);

      var controller = createController(section);
      if (controller) {
        controllers.push(controller);
        controller.apply();
      }
    }
  }

  // D76's ratified scroll pattern: a passive scroll listener, throttled through
  // requestAnimationFrame, deciding from getBoundingClientRect(). Deliberately
  // not an IntersectionObserver — that would change shipped behaviour.
  window.addEventListener('scroll', function () { schedule(null); }, { passive: true });
  window.addEventListener('resize', function () { schedule(null); }, { passive: true });

  document.addEventListener('shopify:section:load', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.StickyAtb = {
    refresh: function (section) {
      if (!section) return;
      var controller = find(section);
      if (!controller) return;
      schedule(controller);
    }
  };
})();

/**
 * Shared sticky buy-panel offset.
 *
 * Keeps a short buy panel from overshooting the viewport bottom while stuck by
 * setting --pdp-buy-panel-sticky-top to min(0, viewport height − content height).
 * The CSS that makes [data-buy-panel-sticky] position:sticky lives in
 * src/css/theme.css as its own plain rule.
 *
 * Lives here rather than inside sections/product.liquid's classic single-form
 * controller, which is gated behind `if (!form || !dataEl) return;`: a form-less
 * template — the gift-card PDP, two forms on one page — would never reach it there,
 * and would leave its buy panel at top:0 under the fixed header. A second, separate
 * module from StickyAtb above rather than folded into it: that module's own docblock
 * scopes it to the reveal state machine only, and the two mechanisms have no shared
 * state.
 *
 * Per-section hooks it resolves:
 *   [data-product-section]   the owning section root
 *   [data-buy-panel-sticky]  the element the CSS above makes position:sticky
 *   [data-buy-panel-content] height source measured against the viewport
 *
 * Public API:
 *   window.BuyPanelSticky.refresh(sectionEl)  recompute one section on demand (a tab
 *                                             switch changes which panel is visible,
 *                                             and therefore the content height, ahead
 *                                             of the ResizeObserver firing).
 */
;(function () {
  'use strict';

  if (window.__buyPanelStickyReady) return;
  window.__buyPanelStickyReady = true;

  function sync(section) {
    var sticky = section.querySelector('[data-buy-panel-sticky]');
    var content = section.querySelector('[data-buy-panel-content]');
    if (!sticky || !content) return;

    if (window.innerWidth < 1024) {
      sticky.style.removeProperty('--pdp-buy-panel-sticky-top');
      return;
    }

    var stickyTop = Math.min(0, window.innerHeight - content.offsetHeight);
    sticky.style.setProperty('--pdp-buy-panel-sticky-top', stickyTop + 'px');
  }

  function syncAll() {
    var sections = document.querySelectorAll('[data-product-section]');
    for (var i = 0; i < sections.length; i++) sync(sections[i]);
  }

  function observeContent() {
    if (!window.ResizeObserver) return;
    var contents = document.querySelectorAll('[data-buy-panel-content]');
    for (var i = 0; i < contents.length; i++) {
      (function (content) {
        var section = content.closest('[data-product-section]');
        if (!section) return;
        new ResizeObserver(function () { sync(section); }).observe(content);
      })(contents[i]);
    }
  }

  function init() {
    syncAll();
    observeContent();
  }

  window.addEventListener('resize', syncAll, { passive: true });
  document.addEventListener('shopify:section:load', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.BuyPanelSticky = {
    refresh: function (section) {
      if (!section) return;
      sync(section);
    }
  };
})();
